'use client'

/**
 * Globaler Error-Boundary (R90 2026-05-18).
 *
 * Fängt jeden React/Render-Fehler in der App ab und zeigt eine freundliche
 * Recovery-UI statt der nativen Browser-Default-Page „This page couldn't load".
 * Ohne diese Datei zeigt der Browser bei Render-Crashes / 5xx-Responses seine
 * eigene Error-Seite, was Tester erschreckt.
 *
 * Next.js App-Router-Konvention: error.tsx ist Client-Component, bekommt
 * `error` + `reset()` Props. Reset versucht das Segment neu zu rendern.
 */

import { useEffect } from 'react'
import { AlertTriangle, RotateCcw, Home } from 'lucide-react'

// R90.3: Crash-Loop-Detection. Bei 3 Crashes in 30 s → SW/Cache nuken + reload.
const CRASH_KEY = 'fgl_crash_log_v1'
const CRASH_WINDOW_MS = 30_000
const CRASH_LIMIT_FOR_HARD_RESET = 3

// 2026-06-20: ChunkLoadError = eindeutig Stale-Bundle. Sofort hard-reset beim
// ersten Crash, nicht erst beim 3. — sonst stranden User auf dem Crash-Screen
// nachdem ein Deploy die Chunk-Hashes rotiert hat (Stéphane-Bug 2cprp8hl).
// r19.55.95 (2026-08-06): "Connection closed." + generisches "Failed to fetch"
// ergänzt — 9 Auto-Reports mit identischem Chunk-Hash 05f_5nzcc6vub aus
// altem Deploy → HTTP/2-Stream vom 404-Server geschlossen. Selber Root-Cause,
// nur andere Fehler-Message.
function isChunkLoadError(error: { name?: string; message?: string; stack?: string }): boolean {
  const blob = `${error?.name || ''} ${error?.message || ''} ${error?.stack || ''}`
  return /ChunkLoadError|Loading chunk \d+ failed|Failed to (load|fetch) (chunk|module)|Failed to fetch|Connection closed\.|Importing a module script failed|webpackChunk/i.test(blob)
}

async function hardReset() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)))
    }
  } catch {}
  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)))
    }
  } catch {}
  try { sessionStorage.clear() } catch {}
  try { localStorage.removeItem(CRASH_KEY) } catch {}
  const url = new URL(window.location.href)
  url.searchParams.set('_hard_reset', String(Date.now()))
  window.location.replace(url.toString())
}

function trackCrashAndMaybeReset(): boolean {
  try {
    const now = Date.now()
    const raw = localStorage.getItem(CRASH_KEY)
    const arr: number[] = raw ? JSON.parse(raw) : []
    const fresh = arr.filter((t) => now - t < CRASH_WINDOW_MS)
    fresh.push(now)
    localStorage.setItem(CRASH_KEY, JSON.stringify(fresh))
    if (fresh.length >= CRASH_LIMIT_FOR_HARD_RESET) {
      void hardReset()
      return true
    }
  } catch {}
  return false
}

export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  // 2026-06-20: ChunkLoadError sofort behandeln — kein Report, kein Counter,
  // kein UI-Render. User sieht direkt einen freundlichen „Neue Version wird
  // geladen"-Screen während Cache leert.
  const chunkError = isChunkLoadError(error)

  useEffect(() => {
    if (chunkError) {
      // r19.55.215: Kein Auto-Report mehr fuer Standard-Stale-Bundle. Der
      // Hard-Reset ist selbstheilend. Reporten NUR bei Loop-Verdacht — wenn
      // `_hard_reset` bereits in der URL steht, hat der Reset gerade eben
      // nicht geholfen (echtes Problem, nicht nur veralteter Chunk).
      let inLoop = false
      try {
        inLoop = !!new URL(window.location.href).searchParams.get('_hard_reset')
      } catch {}
      if (inLoop) {
        try {
          const path = typeof window !== 'undefined' ? window.location.pathname : 'unknown'
          fetch('/api/bug-reports', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              title: `[AUTO] Stale-Bundle LOOP · ${path.slice(0, 60)}`,
              description: `Hard-Reset lief bereits — Crash wiederholt sich. Echtes Problem, nicht nur veralteter Chunk.\nPfad: ${path}\nMessage: ${error?.message || '—'}\nDigest: ${error?.digest || '—'}`,
              severity: 'HIGH', kind: 'BUG', source: 'WEB',
              url: typeof window !== 'undefined' ? window.location.href : null,
              userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
              appVersion: 'web-auto-stale-bundle-loop',
            }),
          }).catch(() => {})
        } catch {}
      }
      void hardReset()
      return
    }
    // R90.2: Payload an /api/bug-reports anpassen (title/description Pflicht)
    try {
      const path = typeof window !== 'undefined' ? window.location.pathname : 'unknown'
      const href = typeof window !== 'undefined' ? window.location.href : null
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent : null
      const vp = typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : null
      fetch('/api/bug-reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: `[AUTO] Render-Crash · ${path.slice(0, 60)}`,
          description: [
            `Auto-Report vom globalen Error-Boundary (R90)`,
            `Pfad: ${path}`,
            `Digest: ${error?.digest || '—'}`,
            `Message: ${error?.message || '—'}`,
            ``,
            `Stack:`,
            (error?.stack || '(kein Stack)').slice(0, 3000),
          ].join('\n'),
          severity: 'HIGH',
          kind: 'BUG',
          source: 'WEB',
          url: href, userAgent: ua, viewport: vp,
          appVersion: 'web-auto-report',
        }),
      }).catch(() => {})
    } catch {}
    console.error('[GlobalError]', error)
    trackCrashAndMaybeReset()
  }, [error, chunkError])

  if (chunkError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg-primary)' }}>
        <div className="max-w-md w-full text-center">
          <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
            style={{
              background: 'color-mix(in oklch, var(--accent) 18%, transparent)',
              border: '2px solid color-mix(in oklch, var(--accent) 40%, transparent)',
            }}>
            <RotateCcw className="h-8 w-8 animate-spin" style={{ color: 'var(--accent)' }} />
          </div>
          <h1 className="text-2xl font-black mb-2" style={{ color: 'var(--text)' }}>
            Neue Version verfügbar
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--muted)' }}>
            Wir laden die Plattform für dich frisch. Einen Moment — du musst nichts tun.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg-primary, #0d1117)' }}>
      <div className="max-w-md w-full text-center">
        <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
          style={{ background: 'var(--state-danger-soft)', border: '2px solid var(--state-danger-bdr)' }}>
          <AlertTriangle className="h-8 w-8" style={{ color: 'var(--state-danger)' }} />
        </div>
        <h1 className="text-2xl font-black mb-2" style={{ color: 'var(--text, #fff)' }}>
          Tut uns leid, kurzer Aussetzer
        </h1>
        <p className="text-sm leading-relaxed mb-1" style={{ color: 'var(--muted, #8b949e)' }}>
          Die Seite hat sich verschluckt. Wir haben es automatisch gemeldet — das passiert
          jetzt nicht zum Spaß.
        </p>
        <p className="text-xs mt-3 mb-6" style={{ color: 'var(--muted, #8b949e)' }}>
          Versuch&apos;s nochmal, meist klappt der zweite Anlauf. Falls nicht: nimm den
          Käfer-Knopf unten und schick uns kurz was du gemacht hast.
        </p>

        {error?.digest && (
          <code className="block text-[10px] mb-4 px-3 py-1.5 rounded font-mono break-all"
            style={{ background: 'var(--bg-secondary, #161b22)', color: 'var(--muted, #8b949e)' }}>
            Ref: {error.digest}
          </code>
        )}

        <div className="flex flex-wrap gap-2 justify-center">
          <button onClick={() => reset()}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-bold text-sm"
            style={{
              background: 'color-mix(in oklch, var(--accent) 15%, transparent)',
              color: 'var(--accent)',
              border: '1px solid color-mix(in oklch, var(--accent) 40%, transparent)',
            }}>
            <RotateCcw className="h-4 w-4" /> Nochmal versuchen
          </button>
          <a href="/"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-bold text-sm"
            style={{ background: 'var(--bg-secondary, #161b22)', color: 'var(--text, #fff)', border: '1px solid var(--border, #30363d)' }}>
            <Home className="h-4 w-4" /> Startseite
          </a>
          <button onClick={() => { void hardReset() }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs"
            title="Service-Worker abmelden, Caches leeren, hart neu laden"
            style={{ background: 'transparent', color: 'var(--muted, #8b949e)', border: '1px solid var(--border, #30363d)' }}>
            Hart neu laden (Cache leeren)
          </button>
        </div>
      </div>
    </div>
  )
}
